import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Event from '../../../lib/EventEmitter';
import { RefetchBlock } from '../../molecules/RefetchBlock';
import { isHidden } from '../../../lib/BlockUtils/BlockUtils';
import { FetchType } from './constants';
import { getRequestIdFromHeaders } from '../../../lib/HeaderUtils/RequestID';
import { LoadingSpinner } from '../../atoms/LoadingSpinner';

type WithBlockFetcherType = {
  additionalProps?: any;
  blockLayout?: string;
  browserPushEventId?: string;
  elementType?: string;
  embedded?: any;
  fetchType?: string;
  fetchUrl?: string;
  timeout?: number;
  uuid?: string;
  groupLayout?: string;
};

/**
 * HOC for fetching Blocks async.
 * Does not rely on forceUpdate or Observers.
 */
const BlockFetcher = () => (WrappedComponent: any) => {
  function WithBlockFetcher({
    additionalProps,
    blockLayout,
    browserPushEventId,
    elementType,
    embedded,
    fetchType = FetchType.INSTANT,
    fetchUrl = '',
    timeout = 5000,
    uuid,
    groupLayout,
  }: WithBlockFetcherType) {
    const isMounted = useRef(false);
    const refreshCount = useRef(0);

    const [state, setState] = useState<{
      fetchResponseProps: any;
      currentlyLoading: boolean;
      error: boolean;
      requestId?: string;
      refresh?: boolean;
    }>({
      fetchResponseProps: null,
      currentlyLoading: false,
      error: false,
      refresh: false,
    });

    const fetchData = (refresh = false) => {
      // dont show spinner in polling mode
      if (!state.currentlyLoading) {
        setState({ ...state, currentlyLoading: true, error: false, refresh });
      }
    };

    const registerEvents = () => {
      if (elementType) {
        Event.register(elementType, fetchData);
      }
    };

    const removeRegisteredEvents = () => {
      if (elementType) {
        Event.remove(elementType);
      }
    };

    useEffect(() => {
      if (state.currentlyLoading) {
        // the Promise we send out to fetch a component
        const axiosCall = axios.get(fetchUrl, {
          validateStatus(status) {
            return status >= 200 && status < 400; // default
          },
          timeout,
        });

        // the minimum amount of time the re-fetch block will be shown
        const delay = new Promise(r => {
          setTimeout(r, 500);
        });

        Promise.all([axiosCall, delay])
          .then(res => {
            const response = res[0];
            if (isMounted.current) {
              if (response.status === 200) {
                if (state.refresh) {
                  refreshCount.current += 1;
                }
                setState({
                  ...state,
                  fetchResponseProps: {
                    embedded: response.data,
                    responseUrl: response.config.url,
                  },
                  currentlyLoading: false,
                  error: false,
                  refresh: false,
                });
              }
              // for the rare case the loading spinner is on and we get a 304
              if (response.status === 304 && state.currentlyLoading) {
                setState({ ...state, currentlyLoading: false, refresh: false });
              }
            }
          })
          .catch(error => {
            if (isMounted.current) {
              setState({
                ...state,
                currentlyLoading: false,
                error: true,
                requestId: getRequestIdFromHeaders(error?.response?.headers),
                refresh: false,
              });
            }
          });
      }
      // state is not included, would introduce an infinite render-loop
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchUrl, state.currentlyLoading, timeout]);

    // We should check whether the props from future calls are different from the current props,
    // and only render on changed props This needs an hash for props, performance reasons.
    useEffect(() => {
      registerEvents();

      isMounted.current = true;
      if (fetchType === FetchType.LAZY) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchData();
      }

      return () => {
        // This allows us to prevent setting state on an unmounted component.
        // @see https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
        isMounted.current = false;
        removeRegisteredEvents();
      };
      // only on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (state.error) {
      return <RefetchBlock requestId={state.requestId} refetchBlock={fetchData} />;
    }
    if (isHidden({ fetchType, embedded }) && !state.fetchResponseProps) {
      return null;
    }

    let props;

    if (!state.fetchResponseProps || fetchUrl !== state.fetchResponseProps.responseUrl) {
      props = embedded;
    } else {
      props = state.fetchResponseProps.embedded;
    }

    if (props || state.currentlyLoading) {
      return (
        <div className={state.currentlyLoading ? 'block-fetcher block-fetcher--loading' : 'block-fetcher'}>
          {state.currentlyLoading && (
            <div
              className={props ? 'block-fetcher__spinner block-fetcher__spinner--overlap' : 'block-fetcher__spinner'}
            >
              <div className="block-fetcher__spinner-container">
                <LoadingSpinner />
              </div>
            </div>
          )}
          {props && (
            <WrappedComponent
              uuid={uuid}
              elementType={elementType}
              blockLayout={blockLayout}
              groupLayout={groupLayout}
              refresh={() => fetchData(true)}
              refreshCount={refreshCount.current}
              {...props}
              {...additionalProps}
              fetchUrl={fetchUrl}
              browserPushEventId={browserPushEventId}
            />
          )}
        </div>
      );
    }
    return null;
  }

  return WithBlockFetcher;
};

export default BlockFetcher;
